// ============================================================================
// CENTRALIZED ROLE CHECKING HOOK
// ============================================================================
//
// PURPOSE: Consolidate all role checking logic to eliminate duplication
//
// FEATURES:
// - Single source of truth for role checking
// - Backward compatibility with both old and new role systems
// - Memoized for performance
// - Type-safe role checking
//
// USAGE:
// - Replace all inline role checking with this hook
// - Provides consistent role determination across the app
// - Works with both appUser.role and appUser.roles.name
//
// ============================================================================

import { useMemo } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import {
  USER_ROLES,
  isEmployeeRole,
  isAdminRole,
  isCRMRole,
  isClientRole,
} from "@/lib/constants/user-types";

export interface RoleCheckResult {
  // Role names
  roleName: string | null;
  userType: string | null;

  // Role type checks
  isEmployee: boolean;
  isAdmin: boolean;
  isCRM: boolean;
  isClientUser: boolean;

  // Specific role checks
  isWTCEmployee: boolean;
  isWTCAdmin: boolean;
  isWTCCRM: boolean;

  /**
   * @deprecated Always returns `false` — `roles.permissions` is an empty jsonb
   * column nothing writes. See the implementation note in `useRoleCheck`.
   */
  hasPermission: (permission: string) => boolean;

  // Quick access flags
  canAccessCRM: boolean;
  canAccessAdmin: boolean;
  canAccessEmployeeFeatures: boolean;
  canAccessClientFeatures: boolean;
}

/**
 * Centralized hook for all role checking logic
 * Consolidates role determination to eliminate duplication
 */
export function useRoleCheck(): RoleCheckResult {
  const { appUser } = useUniversalAuth();

  return useMemo(() => {
    // Get role name from either new or legacy system
    const roleName = appUser?.roles?.name || appUser?.role || null;
    const userType = roleName; // For backward compatibility

    // Role type checks using centralized functions
    const isWTCEmployee = roleName === USER_ROLES.WTC_EMPLOYEE;
    const isWTCAdmin = roleName === USER_ROLES.WTC_ADMIN;
    const isWTCCRM = roleName === USER_ROLES.WTC_CRM;
    const isClientUser = roleName === USER_ROLES.CLIENT_USER;

    // Composite role checks using centralized functions
    const isEmployee = isEmployeeRole(roleName);
    const isAdmin = isAdminRole(roleName);
    const isCRM = isCRMRole(roleName);

    // Reads the `roles.permissions` jsonb column, which DEFAULTS TO `{}` and is
    // written by nothing — no SQL policy reads it and no other TypeScript does.
    // So this returns `false` for every key, for every user, today. It is kept
    // (deprecated) rather than deleted because removing an exported accessor is
    // a behaviour change and the call is the owner's; see
    // `docs/development/AUTHZ_CONSOLIDATION_PLAN.md` §7.
    //
    // Do NOT build on this believing it is a permission system. The models Hub
    // actually enforces are the role name plus the per-contact grant tables,
    // and `app_users.sensitive_feature_access` for the admin-layered feature
    // gate (`lib/utils/auth/feature-access.ts`).
    const hasPermission = (permission: string): boolean => {
      return appUser?.roles?.permissions?.[permission] === true;
    };

    // Quick access flags for common use cases
    const canAccessCRM = isCRM || isAdmin;
    const canAccessAdmin = isAdmin;
    const canAccessEmployeeFeatures = isEmployee;
    const canAccessClientFeatures = isClientUser;

    return {
      // Role names
      roleName,
      userType,

      // Role type checks
      isEmployee,
      isAdmin,
      isCRM,
      isClientUser,

      // Specific role checks
      isWTCEmployee,
      isWTCAdmin,
      isWTCCRM,

      // Permission checks
      hasPermission,

      // Quick access flags
      canAccessCRM,
      canAccessAdmin,
      canAccessEmployeeFeatures,
      canAccessClientFeatures,
    };
  }, [appUser?.roles?.name, appUser?.role, appUser?.roles?.permissions]);
}

// ============================================================================
// CONVENIENCE HOOKS FOR SPECIFIC USE CASES
// ============================================================================

/**
 * Hook for employee-specific role checking
 */
export function useEmployeeRoleCheck() {
  const { isEmployee, isAdmin, isCRM, isWTCEmployee, isWTCAdmin, isWTCCRM } = useRoleCheck();

  return {
    isEmployee,
    isAdmin,
    isCRM,
    isWTCEmployee,
    isWTCAdmin,
    isWTCCRM,
  };
}

/**
 * Hook for CRM-specific role checking
 */
export function useCRMRoleCheck() {
  const { isCRM, canAccessCRM, hasPermission } = useRoleCheck();

  return {
    isCRM,
    canAccessCRM,
    hasPermission,
  };
}

/**
 * Hook for admin-specific role checking
 */
export function useAdminRoleCheck() {
  const { isAdmin, canAccessAdmin, hasPermission } = useRoleCheck();

  return {
    isAdmin,
    canAccessAdmin,
    hasPermission,
  };
}

/**
 * Hook for client-specific role checking
 */
export function useClientRoleCheck() {
  const { isClientUser, canAccessClientFeatures } = useRoleCheck();

  return {
    isClientUser,
    canAccessClientFeatures,
  };
}
